import { queryOptions, useMutation } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import type {
  AspirationByDonorDateSiteResponse,
  AspirationChutesideResult,
  AspirationDataEntryRequest,
  AspirationHistoryResponse,
  AspirationsTodayResponse,
  DonorScheduleResponse,
  FertilizationDataResponse,
  PregnantStatusParams,
  ProcedureHistoryResponse,
  QcDataBulkRequest,
  QcDataChutesideResponse,
  QcDataRequest,
  TempQcInfoResponse,
} from './types';

export type {
  AspirationByDonorDateSiteResponse,
  AspirationChutesideResult,
  AspirationDataEntryRequest,
  AspirationHistoryResponse,
  AspirationsTodayResponse,
  DonorScheduleResponse,
  FertilizationDataResponse,
  PregnantStatusParams,
  ProcedureHistoryResponse,
  QcDataBulkRequest,
  QcDataChutesideResponse,
  QcDataRequest,
  TempQcInfoResponse,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(path: string, params?: URLSearchParams): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/aspiration/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/aspiration/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  path: string,
  method: 'POST' | 'PUT',
  body?: unknown,
  params?: URLSearchParams,
): Promise<T> {
  const baseUrl = `${getBaseUrl()}/api/v1/aspiration/${path}`;
  const url = params ? `${baseUrl}?${params.toString()}` : baseUrl;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchTodaysAspirations(params: {
  date: string;
  siteId: string;
  vetId?: string;
  techId?: string;
  type: number;
  location?: string;
}): Promise<AspirationsTodayResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  if (params.vetId) searchParams.set('vetId', params.vetId);
  if (params.techId) searchParams.set('techId', params.techId);
  searchParams.set('type', String(params.type));
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<AspirationsTodayResponse[]>('today', searchParams);
}

async function fetchAspirationByDonorDateSite(params: {
  donorId: string;
  date: string;
  siteId: string;
}): Promise<AspirationByDonorDateSiteResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorId', params.donorId);
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  return fetchGet<AspirationByDonorDateSiteResponse[]>(
    'by-donor-date-site',
    searchParams,
  );
}

async function fetchTempQcInfo(params: {
  chuteNumber: number;
  siteId: string;
  aspirationDate: string;
  location?: string;
}): Promise<TempQcInfoResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('chuteNumber', String(params.chuteNumber));
  searchParams.set('siteId', params.siteId);
  searchParams.set('aspirationDate', params.aspirationDate);
  if (params.location) searchParams.set('location', params.location);
  return fetchGet<TempQcInfoResponse>('temp-qc-info', searchParams);
}

async function fetchProceduresByDonorId(params: {
  donorId: string;
  ranch?: string;
}): Promise<ProcedureHistoryResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `procedures/${params.donorId}`;
  return fetchGet<ProcedureHistoryResponse[]>(
    path,
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchQcDataChuteside(params: {
  date: string;
  siteId: string;
  location: string;
}): Promise<QcDataChutesideResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('date', params.date);
  searchParams.set('siteId', params.siteId);
  searchParams.set('location', params.location);
  return fetchGet<QcDataChutesideResponse>('qc-data', searchParams);
}

async function fetchFertDataByDonorId(params: {
  donorId: string;
  ranch?: string;
}): Promise<FertilizationDataResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `fert-data/${params.donorId}`;
  return fetchGet<FertilizationDataResponse[]>(
    path,
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchAspirationHistory(params: {
  donorId: string;
  aspirationDate: string;
  ranch?: string;
}): Promise<AspirationHistoryResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `history/${params.donorId}`;
  return fetchGet<AspirationHistoryResponse[]>(path, searchParams);
}

async function fetchDonorSchedule(params: {
  donorId: string;
  aspirationDate: string;
  ranch?: string;
}): Promise<DonorScheduleResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  if (params.ranch) searchParams.set('ranch', params.ranch);
  const path = `donor-schedule/${params.donorId}`;
  return fetchGet<DonorScheduleResponse[]>(path, searchParams);
}

export const todaysAspirationsOptions = (params: {
  date: string;
  siteId: string;
  vetId?: string;
  techId?: string;
  type: number;
  location?: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'today', params],
    async queryFn() {
      return fetchTodaysAspirations(params);
    },
  });

export const aspirationByDonorDateSiteOptions = (params: {
  donorId: string;
  date: string;
  siteId: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'by-donor-date-site', params],
    async queryFn() {
      return fetchAspirationByDonorDateSite(params);
    },
  });

export const tempQcInfoOptions = (params: {
  chuteNumber: number;
  siteId: string;
  aspirationDate: string;
  location?: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'temp-qc-info', params],
    async queryFn() {
      return fetchTempQcInfo(params);
    },
  });

export const proceduresByDonorIdOptions = (params: {
  donorId: string;
  ranch?: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'procedures', params],
    async queryFn() {
      return fetchProceduresByDonorId(params);
    },
  });

export const qcDataChutesideOptions = (params: {
  date: string;
  siteId: string;
  location: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'qc-data', params],
    async queryFn() {
      return fetchQcDataChuteside(params);
    },
  });

export const fertDataByDonorIdOptions = (params: {
  donorId: string;
  ranch?: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'fert-data', params],
    async queryFn() {
      return fetchFertDataByDonorId(params);
    },
  });

export const aspirationHistoryOptions = (params: {
  donorId: string;
  aspirationDate: string;
  ranch?: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'history', params],
    async queryFn() {
      return fetchAspirationHistory(params);
    },
  });

export const donorScheduleOptions = (params: {
  donorId: string;
  aspirationDate: string;
  ranch?: string;
}) =>
  queryOptions({
    queryKey: ['aspiration', 'donor-schedule', params],
    async queryFn() {
      return fetchDonorSchedule(params);
    },
  });

async function updateAspirationChutesideFn(
  input: AspirationDataEntryRequest,
  isReturn: string,
): Promise<AspirationChutesideResult> {
  const params = new URLSearchParams();
  params.set('isReturn', isReturn);
  return fetchMutation<AspirationChutesideResult>(
    'chuteside',
    'PUT',
    input,
    params,
  );
}

async function updateQcDataFn(input: QcDataRequest): Promise<boolean> {
  return fetchMutation<boolean>('qc-data', 'PUT', input);
}

async function updateDonorLastBredFn(params: {
  donorId: string;
  lastBredDate: string;
}): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorId', params.donorId);
  searchParams.set('lastBredDate', params.lastBredDate);
  return fetchMutation<boolean>(
    'donor-last-bred',
    'PUT',
    undefined,
    searchParams,
  );
}

async function updateQcDataBulkFn(
  input: QcDataBulkRequest,
  params: { aspirationDate: string; siteId: string; location: string },
): Promise<boolean> {
  const searchParams = new URLSearchParams();
  searchParams.set('aspirationDate', params.aspirationDate);
  searchParams.set('siteId', params.siteId);
  searchParams.set('location', params.location);
  return fetchMutation<boolean>('qc-data-bulk', 'PUT', input, searchParams);
}

async function addPregnantStatusFn(
  params: PregnantStatusParams,
): Promise<unknown> {
  const searchParams = new URLSearchParams();
  searchParams.set('pregnantValue', String(params.pregnantValue));
  searchParams.set('donorId', params.donorId);
  searchParams.set('opuTechId', params.opuTechId);
  searchParams.set('chuteTechId', params.chuteTechId);
  return fetchMutation<unknown>(
    'pregnant-status',
    'POST',
    undefined,
    searchParams,
  );
}

export function useUpdateAspirationChutesideMutation() {
  return useMutation({
    mutationFn: ({
      input,
      isReturn,
    }: {
      input: AspirationDataEntryRequest;
      isReturn: string;
    }) => updateAspirationChutesideFn(input, isReturn),
  });
}

export function useUpdateQcDataMutation() {
  return useMutation({
    mutationFn: (input: QcDataRequest) => updateQcDataFn(input),
  });
}

export function useUpdateDonorLastBredMutation() {
  return useMutation({
    mutationFn: (params: { donorId: string; lastBredDate: string }) =>
      updateDonorLastBredFn(params),
  });
}

export function useUpdateQcDataBulkMutation() {
  return useMutation({
    mutationFn: ({
      input,
      aspirationDate,
      siteId,
      location,
    }: {
      input: QcDataBulkRequest;
      aspirationDate: string;
      siteId: string;
      location: string;
    }) => updateQcDataBulkFn(input, { aspirationDate, siteId, location }),
  });
}

export function useAddPregnantStatusMutation() {
  return useMutation({
    mutationFn: (params: PregnantStatusParams) => addPregnantStatusFn(params),
  });
}
